import { Observation } from '../../../Services/API';
import { Forecast } from '../../../Services/API/forecastApi';
import {
  WorkRestIntensity,
  WorkRestMatrix,
  WorkRestModifier,
  WorkRestProfile,
} from '../../../Services/API/workRestApi';
import moment from 'moment';
import { Serie } from '@nivo/line';
import { ForecastOptions } from '../../../Constants/WorkRestConstants';
import { RiskProfile } from '../../../Services/API/riskProfileApi';
import { getHighForecastHourRange, getHighForecastRangeString } from './RiskProfile/RiskProfileHelpers';

export const getCurrentObservationValue = (
  observationData: Observation | undefined,
  workRestProfile: WorkRestProfile | undefined
): number | undefined => {
  if (!workRestProfile) return undefined;
  if (workRestProfile.primaryMetric === 'WBGT') {
    return observationData?.data?.wgbt?.value;
  }
  if (workRestProfile.primaryMetric === 'Temperature') {
    return observationData?.data?.ambientTemperature?.value;
  }
  if (workRestProfile.primaryMetric === 'Heat Index') {
    return observationData?.data?.feelLike?.value;
  }
  return undefined;
};

export const getCurrentForecastValue = (
  forecast: Forecast,
  workRestProfile: WorkRestProfile | undefined
): number | undefined => {
  if (!workRestProfile) return undefined;
  if (workRestProfile.primaryMetric === 'WBGT') {
    return forecast.wbgt?.value;
  }
  if (workRestProfile.primaryMetric === 'Temperature') {
    return forecast.ambientTemperature?.value;
  }
  if (workRestProfile.primaryMetric === 'Heat Index') {
    return forecast.feelLike?.value;
  }
  return undefined;
};

export const getWorkRestMatrix = (
  data: WorkRestProfile | undefined,
  value: number | undefined,
  intensity: WorkRestIntensity | undefined
): WorkRestMatrix | undefined => {
  if (!data || value === undefined) return undefined;
  return data.matrices
    .filter(m => m.workRestIntensityId === intensity?.id)
    .sort((a, b) => (a.max ?? Infinity) - (b.max ?? Infinity))
    .find(m => m.max === null || value <= m.max);
};

export const getLabels = (
  profile: WorkRestProfile | undefined,
  matrix: WorkRestMatrix | undefined,
  durationType: 'unit' | 'percent' = 'unit'
): { leftLabel: string; rightLabel: string } => {
  if (!profile || !matrix) return { leftLabel: '--', rightLabel: '--' };

  const work = matrix.workPercent;
  const rest = 100 - work;

  if (durationType === 'percent') {
    return { leftLabel: `${work}%`, rightLabel: `${rest}%` };
  }

  if (profile.durationUnit === 'Hour') {
    const workTime = Math.round(60 * (matrix.workPercent / 100));
    const restTime = Math.round(60 * (1 - matrix.workPercent / 100));
    const stopText = profile.customStopText?.trim() || 'Stop Work';
    const continuousText = profile.customContinuousText?.trim() || 'Continuous Work';

    return {
      leftLabel: workTime === 60 ? continuousText : `${workTime}m`,
      rightLabel: restTime === 60 ? stopText : `${restTime}m`,
    };
  }

  return { leftLabel: '--', rightLabel: '--' };
};

export const getXDomainForSelectedOptions = (
  currentForecastData: Forecast[]
): Record<ForecastOptions, [number, number]> => {
  if (!currentForecastData || currentForecastData.length === 0) {
    return {
      [ForecastOptions.Today]: [0, 0],
      [ForecastOptions.Tomorrow]: [0, 0],
      [ForecastOptions.DayAfterTomorrow]: [0, 0],
    };
  }

  const firstForecastHour = moment(currentForecastData[0]?.observationTime);
  const now = moment();

  const hoursLeftToday = Math.max(0, Math.abs(firstForecastHour.diff(now.clone().endOf('day'), 'hours')));

  const todayEnd = hoursLeftToday;
  const tomorrowStart = todayEnd + 1;
  const tomorrowEnd = tomorrowStart + 23;
  const dayAfterTomorrowStart = tomorrowEnd + 1;
  const dayAfterTomorrowEnd = dayAfterTomorrowStart + 23;

  return {
    [ForecastOptions.Today]: [0, todayEnd],
    [ForecastOptions.Tomorrow]: [tomorrowStart, tomorrowEnd],
    [ForecastOptions.DayAfterTomorrow]: [dayAfterTomorrowStart, dayAfterTomorrowEnd],
  };
};

export const findClosestObservationTime = (series: Serie[]): string | undefined => {
  if (!series || series.length === 0 || !series[0].data) return undefined;

  const now = moment();

  const closest = series[0].data.reduce((prev, curr) => {
    const prevDiff = Math.abs(now.diff(moment(prev.x)));
    const currDiff = Math.abs(now.diff(moment(curr.x)));
    return currDiff < prevDiff ? curr : prev;
  });

  return closest.x as string;
};

export const findForecastByTime = (data: Forecast[], time: string) => {
  return data.find(f => moment(f.observationTime).isSame(moment(time)));
};

export const getWorkRestForecastDescription = (
  observationValue: number | undefined,
  totalAdjustment: number,
  intensity: WorkRestIntensity | undefined,
  workRestMatrix: WorkRestMatrix | undefined,
  hourlyForecast: Forecast[] | undefined,
  riskProfile: RiskProfile | undefined,
  workRestProfile: WorkRestProfile | undefined
): string => {
  if (!observationValue || !intensity || !workRestMatrix || !hourlyForecast) {
    return '';
  }

  const currentValue = formatTemperature(observationValue);
  const modifierValue = formatTemperature(totalAdjustment);

  const highForecastInfo = getHighForecastHourRange(hourlyForecast, riskProfile, workRestProfile, totalAdjustment);
  const highForecastString = getHighForecastRangeString(workRestProfile?.primaryMetric, highForecastInfo);

  const workSuggestion = getWorkSuggestionString(intensity, workRestMatrix);

  if (totalAdjustment === 0) {
    return `${workRestProfile?.primaryMetric} is ${currentValue}. ${workSuggestion} ${highForecastString}`;
  }

  return `${workRestProfile?.primaryMetric} is ${currentValue}, plus ${modifierValue} for clothing. ${workSuggestion} ${highForecastString}`;
};

export const getWorkSuggestionString = (intensity: WorkRestIntensity, workRestMatrix: WorkRestMatrix) => {
  if (!intensity || !workRestMatrix || workRestMatrix.workPercent === undefined) {
    return '';
  }

  if (workRestMatrix.workPercent === 100) {
    return `No limitations for ${intensity.name.toLowerCase()} work, continue activity as usual.`;
  }
  if (workRestMatrix.workPercent === 0) {
    return `Use extreme caution, limit ${intensity.name.toLowerCase()} work to only critical activity.`;
  }

  const restMinutes = 60 * (1 - (workRestMatrix.workPercent ?? 100) / 100);
  return `For ${intensity.name.toLowerCase()} work, rest for a total of ${restMinutes} minutes per hour.`;
};

export const getModifierActions = (
  workRestProfile: WorkRestProfile | undefined,
  currentModifiers: WorkRestModifier[]
) => {
  if (!workRestProfile) return [];

  return workRestProfile.modifiers.map(modifier => ({
    id: modifier.id,
    name: modifier.name,
    title: modifier.adjustment >= 0 ? `+${modifier.adjustment.toFixed(1)}°F` : `${modifier.adjustment.toFixed(1)}°F`,
    selected: currentModifiers.some(m => m.id === modifier.id),
  }));
};

export const getIntensityActions = (
  workRestProfile: WorkRestProfile | undefined,
  currentIntensity: WorkRestIntensity | undefined
) => {
  if (!workRestProfile) return [];

  return workRestProfile.intensities.map(intensity => ({
    id: intensity.id,
    name: intensity.name,
    title: intensity.name,
    selected: intensity.id === currentIntensity?.id,
  }));
};

export const formatTemperature = (value?: number): string => {
  if(!value) return '--';

  const rounded = parseFloat(value.toFixed(1));
  const isWhole = Math.abs(rounded % 1) < 0.01;
  return isWhole ? `${Math.round(rounded)}°F` : `${rounded.toFixed(1)}°F`;
};